// AuthContext.js
import React, { createContext, useContext, useEffect, useRef } from 'react';
import AppContext from './Context';
import { getWorkspaceMode } from 'helpers/workspace';
import axiosInstance from 'axiosInstance';
import { attachPushSubscription, detachPushSubscription } from 'helpers/pushNotifications';

const AuthContext = createContext();
const PLAN_GATED_MODULES = new Set([
  'dashboard',
  'analisis',
  'integrations',
  'posts',
  'stock',
  'questions',
  'sales',
  'claims',
  'calendar',
  'activity',
  'messages',
  'agents',
  'notifications',
  'price_calculator'
]);

const normalizePlanModuleName = moduleName => {
  const normalized = String(moduleName || '').trim().replace(/-/g, '_');
  if (normalized === 'ml_messages') {
    return 'messages';
  }
  return normalized;
};

const isCheckoutGatePath = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  return window.location.pathname.startsWith('/subscription/checkout');
};

export function AuthProvider({ children }) {
  const {
    userData,
    setUserData,
    userDispatch,
  } = useContext(AppContext);
  const contextHydratedRef = useRef(false);
  const contextSyncInFlightRef = useRef(false);
  const lastContextSyncAtRef = useRef(0);

  const syncContextFromServer = async ({ force = false } = {}) => {
    if (!isAuthenticated()) {
      return;
    }

    const now = Date.now();
    const minSyncIntervalMs = force ? 15000 : 30000;
    if (now - lastContextSyncAtRef.current < minSyncIntervalMs) {
      return;
    }
    if (contextSyncInFlightRef.current) {
      return;
    }

    contextSyncInFlightRef.current = true;
    try {
      const response = await axiosInstance.get('login/me');
      const auth = response.data.auth || {};
      refresh({
        integrations: response.data.integrations || {},
        providers: response.data.providers || {},
        permissions: auth.permissions || [],
        company: auth.company || null,
        companies: auth.companies || [],
        profile: auth.profile || null,
        membership: auth.membership || null,
        activeIntegration:
          auth.active_integration || response.data.active_integration || null,
        workspace: auth.workspace || null
      });
      lastContextSyncAtRef.current = now;
    } finally {
      contextSyncInFlightRef.current = false;
    }
  };

  const login = (user) => {
    setUserData('userId', user.userId);
    setUserData('userName', user.userName);
    setUserData('userEmail', user.userEmail);
    setUserData('user', user.user || null);
    setUserData('remember', user.remember);
    setUserData('integrations', user.integrations || {});
    setUserData('providers', user.providers || {});
    setUserData('permissions', user.permissions || []);
    setUserData('company', user.company || null);
    setUserData('companies', user.companies || []);
    setUserData('profile', user.profile || null);
    setUserData('membership', user.membership || null);
    setUserData('activeIntegration', user.activeIntegration || null);
    setUserData('workspace', user.workspace || { mode: 'gestion', label: 'Gestion', partner_code: '', partner_label: '' });
    userDispatch({ type: 'REFRESH' });
  };

  const refresh = (user) => {
    if(user.userId != undefined) setUserData('userId', user.userId);
    if(user.userName != undefined) setUserData('userName', user.userName);
    if(user.userEmail != undefined) setUserData('userEmail', user.userEmail);
    if(user.user != undefined) setUserData('user', user.user);
    if(user.remember != undefined) setUserData('remember', user.remember);
    if(user.integrations != undefined) setUserData('integrations', user.integrations);
    if(user.providers != undefined) setUserData('providers', user.providers);
    if(user.permissions != undefined) setUserData('permissions', user.permissions);
    if(user.company != undefined) setUserData('company', user.company);
    if(user.companies != undefined) setUserData('companies', user.companies);
    if(user.profile != undefined) setUserData('profile', user.profile);
    if(user.membership != undefined) setUserData('membership', user.membership);
    if(user.activeIntegration != undefined) setUserData('activeIntegration', user.activeIntegration);
    if(user.workspace != undefined) setUserData('workspace', user.workspace);
    userDispatch({ type: 'REFRESH' });
  };

  const lock = () => {
    // Clear the authentication token and user data
    // Remove the token from local storage or cookies
    // Update the user state to null
    setUserData('userId', -1);
  };

  const logout = async () => {
    try {
      await detachPushSubscription();
    } catch (err) {
      // Continue logout flow even if push unsubscribe fails.
    }

    // Call backend to invalidate the Django session
    try {
      await axiosInstance.post('login/logout');
    } catch (err) {
      // Proceed with local cleanup even if the server call fails
    }
    // Clear local authentication state
    setUserData('userId', -1);
    setUserData('userName', "");
    setUserData('userEmail', "");
    setUserData('user', null);
    setUserData('remember', false);
    setUserData('integrations', {});
    setUserData('providers', {});
    setUserData('permissions', []);
    setUserData('company', null);
    setUserData('companies', []);
    setUserData('profile', null);
    setUserData('membership', null);
    setUserData('activeIntegration', null);
    setUserData('workspace', { mode: 'gestion', label: 'Gestion', partner_code: '', partner_label: '' });
  };

  //Has the ID cleared but the email and name remains, so it can login again with the password
  const isLocked = () => {
    return (userData != null && userData != undefined) &&
    (userData.userId == undefined || userData.userId == -1 ) &&
    (userData.userName != undefined && userData.userName != "" ) &&
    (userData.userEmail != undefined && userData.userEmail != "" )
  }

  //Has the ID set
  const isAuthenticated = () => {
    return (userData != null && userData != undefined && userData.userId != undefined && userData.userId != -1)
  }

  //Has the ID set
  const hasIntegration = (integration) => {
    return isAuthenticated() && (
      userData.integrations != undefined && userData.integrations != null &&
      userData.integrations[integration] != undefined && userData.integrations[integration] != null &&
      userData.integrations[integration]
    )
  }

  const hasPermission = permissionCode => {
    if (isSuperAdmin()) {
      return true;
    }
    return isAuthenticated() &&
      userData.permissions != undefined &&
      userData.permissions != null &&
      Array.isArray(userData.permissions) &&
      userData.permissions.includes(permissionCode);
  }

  const hasAnyPermission = permissionCodes => {
    if (isSuperAdmin()) {
      return true;
    }
    return Array.isArray(permissionCodes) && permissionCodes.some(hasPermission);
  }

  const hasPlanFeature = featureKey => {
    if (isSuperAdmin()) {
      return true;
    }
    const features = userData?.company?.account?.plan_features;
    if (!features) return true; // no plan info yet → allow (fail-open for UX)
    return features[featureKey] === true;
  }

  const hasPlanModule = moduleName => {
    if (isSuperAdmin()) {
      return true;
    }
    const normalizedModule = normalizePlanModuleName(moduleName);
    if (!PLAN_GATED_MODULES.has(normalizedModule)) {
      return true;
    }
    const modules = userData?.company?.account?.plan_modules;
    if (!Array.isArray(modules) || !modules.length) {
      return true;
    }
    return modules.includes(normalizedModule);
  }

  const isSuperAdmin = () => {
    return isAuthenticated() &&
      userData.user != null &&
      userData.user.is_super_admin === true;
  }

  const getCurrentWorkspaceMode = () => getWorkspaceMode(userData);

  const switchContext = async ({ companyId, integrationId } = {}) => {
    const response = await axiosInstance.post('login/switch-context', {
      company_id: companyId,
      integration_id: integrationId
    });
    const auth = response.data.auth || {};
    refresh({
      integrations: response.data.integrations || {},
      providers: response.data.providers || {},
      permissions: auth.permissions || [],
      company: auth.company || null,
      companies: auth.companies || [],
      profile: auth.profile || null,
      membership: auth.membership || null,
      activeIntegration: auth.active_integration || response.data.active_integration || null,
      workspace: auth.workspace || null
    });
    return response.data;
  };

  const SUPPORT_SNAPSHOT_KEY = 'support_masquerade_snapshot';

  const enterSupportSession = async (sessionId) => {
    const response = await axiosInstance.get(`login/support/sessions/${sessionId}/enter`);
    const data = response.data;
    const auth = data.auth || {};

    // Save current context so we can restore it on exit
    const snapshot = {
      user: userData.user,
      userId: userData.userId,
      userName: userData.userName,
      userEmail: userData.userEmail,
      integrations: userData.integrations,
      providers: userData.providers,
      permissions: userData.permissions,
      company: userData.company,
      companies: userData.companies,
      profile: userData.profile,
      membership: userData.membership,
      activeIntegration: userData.activeIntegration,
      workspace: userData.workspace,
    };
    sessionStorage.setItem(SUPPORT_SNAPSHOT_KEY, JSON.stringify(snapshot));
    sessionStorage.setItem('support_session', JSON.stringify(data.support_session));

    refresh({
      user: auth.user || null,
      integrations: data.integrations || {},
      providers: data.providers || {},
      permissions: auth.permissions || [],
      company: auth.company || null,
      companies: auth.companies || [],
      profile: auth.profile || null,
      membership: auth.membership || null,
      activeIntegration: data.active_integration || null,
      workspace: auth.workspace || null,
    });

    return data.support_session;
  };

  const exitSupportSession = async () => {
    const raw = sessionStorage.getItem(SUPPORT_SNAPSHOT_KEY);
    if (!raw) return;

    try {
      await axiosInstance.post('login/support/masquerade/exit');
    } catch (err) {
      // Even if backend cleanup fails, restore local support context to avoid lock-in.
    }

    const snapshot = JSON.parse(raw);
    sessionStorage.removeItem(SUPPORT_SNAPSHOT_KEY);
    sessionStorage.removeItem('support_session');
    refresh(snapshot);
  };

  const getActiveSupportSession = () => {
    const raw = sessionStorage.getItem('support_session');
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  };

  useEffect(() => {
    if (!isAuthenticated()) {
      contextHydratedRef.current = false;
      contextSyncInFlightRef.current = false;
      lastContextSyncAtRef.current = 0;
    }
  }, [userData.userId]);

  useEffect(() => {
    if (!isAuthenticated()) {
      return undefined;
    }

    if (!hasAnyPermission([
      'notifications.view_internal',
      'notifications.view_mercadolibre',
      'notifications.view_questions'
    ])) {
      return undefined;
    }

    let cancelled = false;

    const ensurePushSubscription = async () => {
      try {
        await attachPushSubscription(userData.userId);
      } catch (error) {
        if (!cancelled) {
          // Best-effort setup; avoid interrupting auth/session flow.
        }
      }
    };

    ensurePushSubscription();

    return () => {
      cancelled = true;
    };
  }, [userData.userId, userData.permissions]);

  useEffect(() => {
    const shouldHydrateContext =
      isAuthenticated() &&
      !contextHydratedRef.current;

    if (!shouldHydrateContext) {
      return undefined;
    }

    contextHydratedRef.current = true;
    let cancelled = false;

    syncContextFromServer({ force: true }).catch(() => {
      if (!cancelled) {
        contextHydratedRef.current = false;
      }
    });

    return () => {
      cancelled = true;
    };
  }, [userData.userId]);

  useEffect(() => {
    if (!isAuthenticated()) {
      return undefined;
    }

    const handleVisibilityChange = () => {
      if (isCheckoutGatePath()) {
        return;
      }
      if (document.visibilityState === 'visible') {
        syncContextFromServer().catch(() => {});
      }
    };

    const handleFocus = () => {
      if (isCheckoutGatePath()) {
        return;
      }
      syncContextFromServer().catch(() => {});
    };

    const handlePopState = () => {
      if (isCheckoutGatePath()) {
        return;
      }
      syncContextFromServer({ force: true }).catch(() => {});
    };

    const handlePageShow = event => {
      if (isCheckoutGatePath()) {
        return;
      }
      if (event?.persisted) {
        syncContextFromServer({ force: true }).catch(() => {});
      }
    };

    window.addEventListener('focus', handleFocus);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [userData.userId]);

  return (
    <AuthContext.Provider value={{ userData, login, refresh, lock, logout, isLocked, isAuthenticated, hasIntegration, hasPermission, hasAnyPermission, hasPlanFeature, hasPlanModule, isSuperAdmin, getCurrentWorkspaceMode, switchContext, enterSupportSession, exitSupportSession, getActiveSupportSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
